import React, { useState } from "react";
import { Link } from "react-router-dom";

const THREAT_STYLES = {
  high: "bg-[#e34234] text-white",
  medium: "bg-[#0a0a0a] text-white",
  low: "bg-white text-[#0a0a0a] border border-[#0a0a0a]",
};

const THREAT_LABEL = {
  high: "High threat",
  medium: "Watch",
  low: "Low",
};

function slugify(name) {
  return name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

export default function CompetitorCard({ competitor, onSaveNotes }) {
  const [notes, setNotes] = useState(competitor.notes || "");
  const [savedState, setSavedState] = useState("idle");
  const dirty = notes !== (competitor.notes || "");

  const handleSave = async () => {
    setSavedState("saving");
    const ok = await onSaveNotes(competitor.id, notes);
    setSavedState(ok ? "saved" : "error");
    if (ok) setTimeout(() => setSavedState("idle"), 1800);
  };

  return (
    <article
      className="border border-[#0a0a0a] p-6 bg-white"
      data-testid={`competitor-card-${competitor.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display uppercase text-2xl tracking-tight">
            <Link
              to={`/admin/market/competitors/${slugify(competitor.name)}`}
              className="hover:text-[#e34234] transition-colors"
              data-testid={`competitor-name-link-${competitor.id}`}
            >
              {competitor.name} <span className="text-[#9a9a9a]">→</span>
            </Link>
          </h2>
          <div className="font-mono-tiny text-[#6b6b6b] mt-1">
            {competitor.base}
            {competitor.url && (
              <>
                {" · "}
                <a
                  href={competitor.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vbb-link"
                  data-testid={`competitor-url-${competitor.id}`}
                >
                  {competitor.url.replace(/^https?:\/\//, "")} ↗
                </a>
              </>
            )}
          </div>
        </div>
        <span
          className={`px-3 py-1 font-mono-tiny uppercase tracking-wider ${THREAT_STYLES[competitor.threat] || THREAT_STYLES.medium}`}
          data-testid={`competitor-threat-${competitor.id}`}
        >
          {THREAT_LABEL[competitor.threat] || competitor.threat}
        </span>
      </header>

      <p className="text-[15px] leading-relaxed text-[#2a2a2a]">
        {competitor.positioning}
      </p>

      {(competitor.stack || competitor.pricing) && (
        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-[14px]">
          {competitor.stack && (
            <div>
              <dt className="vbb-overline mb-1">Stack</dt>
              <dd className="text-[#2a2a2a] leading-relaxed">{competitor.stack}</dd>
            </div>
          )}
          {competitor.pricing && (
            <div>
              <dt className="vbb-overline mb-1">Pricing</dt>
              <dd className="text-[#2a2a2a] leading-relaxed">{competitor.pricing}</dd>
            </div>
          )}
        </dl>
      )}

      {competitor.notable_clients && (
        <div className="mt-5 text-[14px]">
          <div className="vbb-overline mb-1">Notable clients</div>
          <p className="text-[#2a2a2a] leading-relaxed">{competitor.notable_clients}</p>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5 text-[14px]">
        {competitor.strengths?.length > 0 && (
          <div>
            <div className="vbb-overline mb-2">Strengths</div>
            <ul className="space-y-1 text-[#2a2a2a]">
              {competitor.strengths.map((s) => (
                <li key={s} className="leading-relaxed">— {s}</li>
              ))}
            </ul>
          </div>
        )}
        {competitor.weaknesses?.length > 0 && (
          <div>
            <div className="vbb-overline mb-2 text-[#e34234]">Where VBB attacks</div>
            <ul className="space-y-1 text-[#2a2a2a]">
              {competitor.weaknesses.map((w) => (
                <li key={w} className="leading-relaxed">— {w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-6 border-t border-[#0a0a0a] pt-4">
        <label
          htmlFor={`notes-${competitor.id}`}
          className="vbb-overline mb-2 block"
        >
          Your notes
        </label>
        <textarea
          id={`notes-${competitor.id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-[#0a0a0a] p-3 font-mono-tiny text-[13px] resize-y focus:outline-none focus:border-[#e34234]"
          placeholder="Add internal notes — pricing teardown, lead conversations, learnings…"
          data-testid={`competitor-notes-${competitor.id}`}
        />
        <div className="mt-2 flex items-center justify-between">
          <span
            className="font-mono-tiny text-[#6b6b6b]"
            data-testid={`competitor-save-state-${competitor.id}`}
          >
            {savedState === "saving" && "Saving…"}
            {savedState === "saved" && "Saved."}
            {savedState === "error" && (
              <span className="text-[#e34234]">Save failed.</span>
            )}
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || savedState === "saving"}
            className="vbb-btn vbb-btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`competitor-save-btn-${competitor.id}`}
          >
            Save notes
          </button>
        </div>
      </div>
    </article>
  );
}
